import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Customer } from '../../../shared/types';
import { CustomerForm, defaultLohnscheinLauf, type LohnscheinLaufFormConfig } from './CustomerForm';
import { supabase } from '../../../shared/services/supabase';
import { useCustomerCreate } from '../hooks/useCustomerCreate';
import { validateCustomerData } from '../utils/customerValidation';
import { customerContactService } from '../../../services/customer/CustomerContactService';

interface CreateCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateCustomerModal: React.FC<CreateCustomerModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<Partial<Customer>>({});
  const [activeTab, setActiveTab] = useState<'stammdaten' | 'lohnlauf' | 'steuer_sozialversicherung'>('stammdaten');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Zwei-Lauf-Modell: Lohnschein-Konfiguration pro Lauf.
  const [lohnscheinConfigs, setLohnscheinConfigs] = useState<LohnscheinLaufFormConfig[]>([defaultLohnscheinLauf(1), defaultLohnscheinLauf(2)]);
  
  const { createCustomer, isCreating } = useCustomerCreate();

  // Initialisiere Formular mit Default-Werten
  useEffect(() => {
    if (isOpen) {
      setFormData({
        // Defaults für neue Kunden
        abrechnungsmethode: 'lug',
        lohnsteuer_zeitraum: 'monatlich',
        versandmethode: 'email',
        anrede: 'Sie',
        geschlecht: 'männlich',
        sv_pflicht: true,
        schaetzmandat: false,
        postalischer_versand: false,
        erster_lohnlauf_monat_offset: 0,
        zweiter_lohnlauf_monat_offset: 0,
        lohnschein_versand_monat_offset: 0,
        lohnschein_ohne_passwort: false,
        erster_lohnlauf_frist_ab_anlieferung: false,
        zweiter_lohnlauf_frist_ab_anlieferung: false
      });
      setErrors({});
      setActiveTab('stammdaten');
      setLohnscheinConfigs([defaultLohnscheinLauf(1), defaultLohnscheinLauf(2)]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFieldChange = (field: keyof Customer, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleLohnscheinConfigChange = (lohnlauf: 1 | 2, patch: Partial<LohnscheinLaufFormConfig>) => {
    setLohnscheinConfigs(prev => prev.map(c => c.lohnlauf === lohnlauf ? { ...c, ...patch } : c));
  };

  const handleSave = async () => {
    // Validierung
    const validationResult = validateCustomerData(formData);
    if (!validationResult.valid) {
      setErrors(validationResult.errors);
      
      // Wechsle zum Tab mit dem ersten Fehler
      const firstErrorField = Object.keys(validationResult.errors)[0];
      if (['company', 'email', 'vorname', 'nachname', 'anrede', 'geschlecht', 'beraternummer', 'mandantennummer', 'abrechnungsmethode'].includes(firstErrorField)) {
        setActiveTab('stammdaten');
      } else if (['erster_lohnlauf_tag', 'zweiter_lohnlauf_tag', 'lohnschein_versand_tag'].includes(firstErrorField)) {
        setActiveTab('lohnlauf');
      } else {
        setActiveTab('steuer_sozialversicherung');
      }
      return;
    }

    try {
      const newCustomer = await createCustomer(formData);

      // Automatisch primären Ansprechpartner aus den Formulardaten erstellen
      if (newCustomer?.id) {
        try {
          await customerContactService.createContact({
            customer_id: newCustomer.id,
            email: formData.email || '',
            vorname: formData.vorname || null,
            nachname: formData.nachname || null,
            anrede: (formData.anrede as 'Du' | 'Sie') || null,
            geschlecht: (formData.geschlecht as 'männlich' | 'weiblich' | 'divers') || null,
            email_cc: formData.email_cc || null,
            is_primary: true,
            receives_finalization_package: true,
            secure_download_passwort: formData.secure_download_passwort || null
          });
          console.log('Primärer Ansprechpartner automatisch erstellt für Kunde:', newCustomer.id);
        } catch (contactError) {
          // Kontakt-Erstellung fehlgeschlagen - Kunde wurde trotzdem erstellt
          console.error('Fehler beim Erstellen des primären Ansprechpartners:', contactError);
        }
      }

      // Zwei-Lauf-Modell: aktive Lohnschein-Konfigurationen pro Lauf anlegen.
      if (newCustomer?.id) {
        for (const cfg of lohnscheinConfigs) {
          if (!cfg.aktiv) continue;
          await supabase.from('customer_lohnschein_versand_configs').upsert({
            customer_id: newCustomer.id,
            lohnlauf: cfg.lohnlauf,
            tag: cfg.modus === 'fest' ? cfg.tag : null,
            verschiebung: cfg.modus === 'fest' ? cfg.verschiebung : null,
            monat_offset: cfg.modus === 'fest' ? (cfg.monat_offset ?? 0) : null,
            bezug_werktage_offset: cfg.modus === 'relativ' ? (cfg.bezug_werktage_offset ?? 1) : null,
            is_active: true
          }, { onConflict: 'customer_id,lohnlauf' });
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating customer:', error);
      // Der Hook setzt bereits den Error-State
    }
  };

  const tabs = [
    { id: 'stammdaten', label: 'Stammdaten' },
    { id: 'lohnlauf', label: 'Lohnlauf' },
    { id: 'steuer_sozialversicherung', label: 'Steuer & Sozialversicherung' }
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>
            Neuen Kunden anlegen
          </h2>
          <button
            onClick={onClose}
            disabled={isCreating}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: isCreating ? 'not-allowed' : 'pointer',
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              if (!isCreating) {
                e.currentTarget.style.backgroundColor = '#F3F4F6';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '0 24px',
          marginTop: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              disabled={isCreating}
              style={{
                padding: '12px 16px',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid #3B82F6' : '2px solid transparent',
                color: activeTab === tab.id ? '#3B82F6' : '#6B7280',
                fontWeight: activeTab === tab.id ? '600' : '400',
                fontSize: '14px',
                cursor: isCreating ? 'not-allowed' : 'pointer',
                marginBottom: '-1px',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          <CustomerForm
            formData={formData}
            activeTab={activeTab}
            errors={errors}
            onChange={handleFieldChange}
            lohnscheinConfigs={lohnscheinConfigs}
            onLohnscheinConfigChange={handleLohnscheinConfigChange}
          />
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            disabled={isCreating}
            style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '6px',
              cursor: isCreating ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isCreating ? 0.6 : 1
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isCreating}
            style={{
              padding: '8px 16px',
              backgroundColor: isCreating ? '#9CA3AF' : '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isCreating ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isCreating ? 'Erstelle...' : 'Kunde anlegen'}
          </button>
        </div>
      </div>
    </div>
  );
};

